import type { ResearchEventKind } from "./verbosity-levels";
import environment from "@app/config/environment";
import { useAuth0 } from "@auth0/auth0-react";
import { useEffect, useRef } from "react";

export interface ResearchEventPayload {
  kind: ResearchEventKind;
  payload: Record<string, unknown>;
}

export interface ResearchSessionStreamConfig {
  sessionId: string | null;
  onEvent: (event: ResearchEventPayload) => void;
  onDone: (donePayload: Record<string, unknown> & { summary_id?: string }) => void;
  onError: (error: string) => void;
}

/**
 * Opens a WebSocket to /ws/research_sessions/<id>/stream/, sends a `start`
 * action, and forwards every event payload to onEvent. Calls onDone when the
 * stream yields a `done` event (or onError on `error`). The hook auto-closes
 * the WS when sessionId becomes null or the component unmounts.
 *
 * The wire shape from the backend chanx consumer is:
 *   { event: { kind: "...", payload: {...} } }
 * Auth is passed as a Bearer token query param, mirroring the agent WS.
 */
export function useResearchSessionStream({ sessionId, onEvent, onDone, onError }: ResearchSessionStreamConfig) {
  const wsRef = useRef<WebSocket | null>(null);
  const handlersRef = useRef({ onEvent, onDone, onError });
  // Sync handler refs in an effect to satisfy react-hooks/refs; handlers are
  // still always current before any async WS callback fires because the effect
  // runs synchronously after every render.
  useEffect(() => {
    handlersRef.current = { onEvent, onDone, onError };
  });

  const { getAccessTokenSilently } = useAuth0();

  useEffect(() => {
    if (!sessionId) {
      return;
    }

    let cancelled = false;

    void (async () => {
      let token: string;
      try {
        token = await getAccessTokenSilently();
      } catch {
        handlersRef.current.onError("Failed to get auth token for research stream");
        return;
      }

      if (cancelled) {
        return;
      }

      const url = buildResearchWsUrl(sessionId, token);
      const ws = new WebSocket(url);
      wsRef.current = ws;

      ws.onopen = () => {
        ws.send(JSON.stringify({ action: "start" }));
      };

      ws.onmessage = msg => {
        try {
          // The chanx ResearchEvent BaseMessage ships as:
          //   { action: "research.event", payload: { kind: "...", payload: {...} } }
          // The inner payload is the ResearchEventPayload pydantic model.
          const parsed = JSON.parse(msg.data as string) as {
            action?: string;
            payload?: { kind?: string; payload?: Record<string, unknown> } | Record<string, unknown>;
            // Older inspected shape — kept as fallbacks for diagnostic resilience:
            event?: ResearchEventPayload;
            kind?: string;
          };

          let event: ResearchEventPayload | undefined;
          if (
            parsed.action === "research.event" &&
            parsed.payload &&
            typeof parsed.payload === "object" &&
            "kind" in parsed.payload &&
            "payload" in parsed.payload
          ) {
            const inner = parsed.payload as { kind: string; payload: Record<string, unknown> };
            event = { kind: inner.kind as ResearchEventKind, payload: inner.payload };
          } else if (parsed.event) {
            event = parsed.event;
          } else if (parsed.kind !== undefined && parsed.payload !== undefined) {
            event = {
              kind: parsed.kind as ResearchEventKind,
              payload: parsed.payload
            };
          }

          if (!event) {
            return;
          }
          handlersRef.current.onEvent(event);
          if (event.kind === "done") {
            handlersRef.current.onDone(event.payload);
          } else if (event.kind === "error") {
            const errorKind = event.payload.error_kind;
            const errorMsg =
              typeof errorKind === "string" || typeof errorKind === "number" ? String(errorKind) : "error";
            handlersRef.current.onError(errorMsg);
          }
        } catch (e) {
          handlersRef.current.onError(e instanceof Error ? e.message : String(e));
        }
      };

      ws.onerror = () => {
        handlersRef.current.onError("WebSocket error");
      };
    })();

    return () => {
      cancelled = true;
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);
}

/**
 * Builds the WebSocket URL for a research session stream.
 * Mirrors the agent WS URL construction in agentWebSocket.ts:
 *   ATI_API_BASE_URL (http/https) → ws/wss + path + token param
 */
function buildResearchWsUrl(sessionId: string, token: string): string {
  const wsBase = environment.ATI_API_BASE_URL.replace(/^http/, "ws");
  return `${wsBase}/ws/research_sessions/${sessionId}/stream/?token=Bearer ${token}`;
}
